import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

// Auto-recover from stale chunk references after a deploy. When the user
// is on an old index.html and clicks something that lazy-imports a chunk
// whose hash no longer exists in dist/, Vite dispatches `vite:preloadError`.
// Reload once (sessionStorage guard prevents an infinite loop if the chunk
// is genuinely broken) so the browser fetches the fresh index.html + new
// chunk hashes. Without this every deploy strands open tabs.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (event) => {
    if (sessionStorage.getItem('ch_preload_reload') === '1') return;
    sessionStorage.setItem('ch_preload_reload', '1');
    event.preventDefault();
    window.location.reload();
  });
  window.addEventListener('load', () => {
    sessionStorage.removeItem('ch_preload_reload');
  });
}

if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tracesSampleRate: 0.1,
    // 10% of all sessions recorded for ambient observability + 100% of
    // sessions where an error fires. Stay under Sentry free-tier replay quota
    // until traffic grows; revisit when MRR > $0.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Mask all user-typed text + inputs by default — emails, CSV preview,
      // discovery names all count as customer data even on sandbox.
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
    ],
    // Scrub Authorization headers + Bearer tokens before they reach the wire —
    // axios attaches config.headers to error.config, which Sentry's React SDK
    // auto-serializes into event extras.
    beforeSend(event) {
      const stripHeaders = (h: unknown): unknown => {
        if (!h || typeof h !== 'object') return h;
        const out: Record<string, unknown> = { ...(h as Record<string, unknown>) };
        for (const k of Object.keys(out)) {
          if (k.toLowerCase() === 'authorization' || k.toLowerCase() === 'cookie') {
            out[k] = '[Filtered]';
          }
        }
        return out;
      };
      if (event.request?.headers) {
        event.request.headers = stripHeaders(event.request.headers) as Record<string, string>;
      }
      for (const breadcrumb of event.breadcrumbs ?? []) {
        if (breadcrumb.data && typeof breadcrumb.data === 'object') {
          if ('headers' in breadcrumb.data) {
            (breadcrumb.data as Record<string, unknown>).headers = stripHeaders(
              breadcrumb.data.headers,
            );
          }
        }
      }
      return event;
    },
  });
}

// Auto-recover from stale lazy-chunk references after a deploy.
//
// vite emits fingerprinted asset names (e.g. GraphView-Bxyz123.js). When we
// deploy a new build, the index.html references new fingerprints but a
// stale browser tab still holds the OLD fingerprints in memory. Navigating
// to any lazy-loaded route then tries to fetch a 404'd chunk and React
// throws "Failed to fetch dynamically imported module" — Sentry.ErrorBoundary
// catches it as "Something went wrong" and the user is stuck.
//
// Standard SPA pattern: detect the chunk-load failure once per session and
// force a full reload so the browser refetches index.html and gets the
// fresh fingerprints. The session-storage gate prevents an infinite reload
// loop if the new bundle ALSO 404s (real deploy breakage, not staleness).
const _CHUNK_RELOAD_KEY = 'cb_chunk_reload_attempted';
function _isStaleChunkError(err: unknown): boolean {
  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : '';
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk \S+ failed/i.test(message)
  );
}
function _handleChunkError(err: unknown): void {
  if (!_isStaleChunkError(err)) return;
  if (sessionStorage.getItem(_CHUNK_RELOAD_KEY)) return;
  sessionStorage.setItem(_CHUNK_RELOAD_KEY, '1');
  window.location.reload();
}
window.addEventListener('error', (e) => _handleChunkError(e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) => _handleChunkError(e.reason));
// Clear the reload-attempt flag on successful navigation — if the user
// browses for >30s without another chunk error, treat the recovery as
// stable.
setTimeout(() => sessionStorage.removeItem(_CHUNK_RELOAD_KEY), 30_000);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
